import Image from 'next/image';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { getMusicPresetPreview } from '@/constants/musicPresets';

import { useMusicT } from './kit';

/** The example's two versions: covers borrowed from two templates. */
const RESULTS = [
  { id: 'acoustic-pop', length: '2:48' },
  { id: 'birthday', length: '3:05' },
];

const card =
  'flex h-full flex-col rounded-[28px] bg-white p-7 shadow-[0_24px_60px_-38px_rgba(40,30,120,0.5)] ring-1 ring-[#0f0f28]/[0.07] sm:p-9 dark:bg-[#14141f] dark:ring-white/10';

/**
 * The two modes of the generator: describe the song, or write it yourself
 * (Custom Mode). Small stills of each, not screenshots.
 */
export default function TwoWays() {
  const { t } = useMusicT();
  const reveal = useReveal<HTMLDivElement>(0.15);

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          size='md'
          title={t('Two ways to make a song.')}
          tail={t('Quick, or exactly yours.')}
        />

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(styles.reveal, 'mt-14 grid gap-6 lg:grid-cols-2')}
        >
          <div className={clsxm(styles.rv, card)} style={delay(0)}>
            <p className='text-sm font-semibold text-primary-600 dark:text-primary-400'>
              {t('Describe it')}
            </p>
            <h3 className={clsxm('mt-2 text-2xl font-semibold', tone.ink)}>
              {t('One sentence, a whole song.')}
            </h3>
            <p className={clsxm('mt-3 text-[16px] leading-7', tone.body)}>
              {t(
                'Write what the song is about. The AI writes the lyrics, picks the sound and sings it. Best when you want something good, fast.',
              )}
            </p>
            <div className='mt-8 flex-1 rounded-2xl bg-[#f6f6fa] p-5 dark:bg-white/[0.04]'>
              <p className={clsxm('text-xs font-semibold', tone.quiet)}>
                {t('Song description')}
              </p>
              <p className={clsxm('mt-2 text-[15px] leading-7', tone.ink)}>
                {t(
                  'A cheerful acoustic song for my sister’s 30th birthday, about road trips and bad karaoke',
                )}
              </p>
              <div className='mt-5 flex flex-wrap gap-2'>
                {[t('With vocals'), t('Female voice'), t('V6')].map((chip) => (
                  <span
                    key={chip}
                    className={clsxm(
                      'rounded-full bg-white px-3 py-1 text-xs font-medium ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.06] dark:ring-white/10',
                      tone.body,
                    )}
                  >
                    {chip}
                  </span>
                ))}
              </div>
              <div className='mt-5 border-t border-[#0f0f28]/[0.06] pt-4 dark:border-white/[0.08]'>
                <p className={clsxm('text-xs font-semibold', tone.quiet)}>
                  {t('Two versions, ready to play')}
                </p>
                <ul className='mt-3 space-y-2'>
                  {RESULTS.map((result, index) => {
                    const preview = getMusicPresetPreview(result.id);
                    return (
                      <li
                        key={result.id}
                        className='flex items-center gap-3 rounded-xl bg-white p-2 ring-1 ring-[#0f0f28]/[0.06] dark:bg-white/[0.05] dark:ring-white/10'
                      >
                        <span className='relative h-10 w-10 flex-none overflow-hidden rounded-lg'>
                          {preview && (
                            <Image
                              src={preview.cover}
                              alt=''
                              fill
                              unoptimized
                              loading='lazy'
                              sizes='40px'
                              className='object-cover'
                            />
                          )}
                        </span>
                        <span
                          className={clsxm(
                            'min-w-0 flex-1 truncate text-sm font-medium',
                            tone.ink,
                          )}
                        >
                          {t('Version {{number}}', { number: index + 1 })}
                        </span>
                        <span
                          className={clsxm(
                            'pe-1 text-xs tabular-nums',
                            tone.quiet,
                          )}
                        >
                          {result.length}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          <div className={clsxm(styles.rv, card)} style={delay(90)}>
            <p className='text-sm font-semibold text-primary-600 dark:text-primary-400'>
              {t('Write it yourself')}
            </p>
            <h3 className={clsxm('mt-2 text-2xl font-semibold', tone.ink)}>
              {t('Your lyrics, your sound.')}
            </h3>
            <p className={clsxm('mt-3 text-[16px] leading-7', tone.body)}>
              {t(
                'Turn on Custom Mode to paste your lyrics, set the style and list the sounds to leave out. Best when every word matters.',
              )}
            </p>
            <div className='mt-8 flex-1 space-y-3 rounded-2xl bg-[#f6f6fa] p-5 dark:bg-white/[0.04]'>
              <div>
                <p className={clsxm('text-xs font-semibold', tone.quiet)}>
                  {t('Style')}
                </p>
                <p
                  className={clsxm('mt-1.5 text-[15px] leading-6', tone.ink)}
                  dir='ltr'
                >
                  warm acoustic pop, fingerpicked guitar, soft female vocals, 90
                  BPM
                </p>
              </div>
              <div>
                <p className={clsxm('text-xs font-semibold', tone.quiet)}>
                  {t('Exclude styles')}
                </p>
                <p
                  className={clsxm('mt-1.5 text-[15px] leading-6', tone.ink)}
                  dir='ltr'
                >
                  autotune, heavy drums
                </p>
              </div>
              <div>
                <p className={clsxm('text-xs font-semibold', tone.quiet)}>
                  {t('Lyrics')}
                </p>
                <p
                  className={clsxm(
                    'mt-1.5 whitespace-pre-line text-[15px] leading-6',
                    tone.ink,
                  )}
                >
                  <span className='font-mono text-xs font-semibold text-primary-600 dark:text-primary-400'>
                    [Verse]
                  </span>
                  {'\n'}
                  {t('Thirty candles, one old car')}
                  {'\n'}
                  <span className='font-mono text-xs font-semibold text-primary-600 dark:text-primary-400'>
                    [Chorus]
                  </span>
                  {'\n'}
                  {t('Sing it loud, sing it wrong')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <ul
          className={clsxm(
            'mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-medium',
            tone.body,
          )}
        >
          {[
            t('Male or female voice, or instrumental'),
            t('Up to 3,000 characters of lyrics on paid plans'),
            t('Three models: V6, V6 Wild and V6 Mini'),
          ].map((point) => (
            <li key={point} className='flex items-center gap-2'>
              <CheckGlyph className='h-4 w-4 text-primary-600 dark:text-primary-400' />
              {point}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
